/**
 * A static drawing of the path the target will trace.
 *
 * Written for the Custom path on smooth pursuit, where the doctor sets three
 * abstract numbers — two frequencies and a phase — and previously had no way to
 * learn what they produced except to run the exercise and watch. Guess, run,
 * adjust, run again is exactly the friction that leaves a feature unused
 * (constraint 4).
 *
 * This file knows NOTHING about any exercise or any path. It asks the
 * definition for an outline and draws whatever points come back. An exercise
 * with no path worth drawing does not implement `pathOutline()` and nothing
 * renders. Adding an exercise with a path still touches its own file and the
 * registry line, and not this one.
 *
 * It shows SHAPE, not size — the outline arrives normalised. How much screen an
 * amplitude uses is answered next to the amplitude field itself, which is where
 * that question is actually asked.
 */

import { useMemo } from 'react';
import type { ExerciseDefinition, ParamValues } from '@pathways/exercises';
import styles from './PathPreview.module.css';

/**
 * The drawing's own proportions. Passed to `pathOutline()` because some paths —
 * corner-to-corner diagonals — are defined relative to the shape of the area
 * they run in, so the outline cannot be computed without knowing it.
 */
const BOX_WIDTH = 200;
const BOX_HEIGHT = 120;
const PADDING = 6;

export function PathPreview({
  definition,
  params,
}: {
  definition: ExerciseDefinition;
  params: ParamValues;
}) {
  const outline = definition.pathOutline;

  const points = useMemo(
    () => (outline ? outline.call(definition, params, BOX_HEIGHT / BOX_WIDTH) : []),
    [outline, definition, params],
  );

  // No outline offered, or a path that does not move — say nothing rather than
  // draw an empty box the doctor has to interpret.
  if (!outline || points.length === 0) return null;

  const halfWidth = BOX_WIDTH / 2 - PADDING;
  const halfHeight = BOX_HEIGHT / 2 - PADDING;

  // ONE scale for both axes. Scaling each axis to fill the box independently
  // would stretch every shape to the box's proportions — a circle would draw
  // as an ellipse and a flattened ellipse would draw as a circle, which is
  // precisely the misreading this drawing exists to prevent. So: fit the
  // outline inside the box on whichever axis binds, and leave the slack.
  let extentX = 0;
  let extentY = 0;
  for (const point of points) {
    extentX = Math.max(extentX, Math.abs(point.x));
    extentY = Math.max(extentY, Math.abs(point.y));
  }
  const scale = Math.min(
    extentX > 0 ? halfWidth / extentX : Number.POSITIVE_INFINITY,
    extentY > 0 ? halfHeight / extentY : Number.POSITIVE_INFINITY,
  );
  if (!Number.isFinite(scale)) return null; // no extent on either axis

  // y is flipped: the outline has y positive upwards, SVG has it downwards.
  const path = points
    .map((point, index) => {
      const x = BOX_WIDTH / 2 + point.x * scale;
      const y = BOX_HEIGHT / 2 - point.y * scale;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <figure className={styles.figure}>
      <svg
        className={styles.canvas}
        viewBox={`0 0 ${BOX_WIDTH} ${BOX_HEIGHT}`}
        role="img"
        aria-label={`Path shape for ${definition.name} at these settings`}
      >
        {/* Centre marks, so a line through the middle is distinguishable from
            a line that merely looks central. */}
        <line
          className={styles.axis}
          x1={BOX_WIDTH / 2}
          y1={PADDING}
          x2={BOX_WIDTH / 2}
          y2={BOX_HEIGHT - PADDING}
        />
        <line
          className={styles.axis}
          x1={PADDING}
          y1={BOX_HEIGHT / 2}
          x2={BOX_WIDTH - PADDING}
          y2={BOX_HEIGHT / 2}
        />
        <path className={styles.path} d={path} />
      </svg>
      <figcaption className={styles.caption}>
        Path shape — proportions only, not size
      </figcaption>
    </figure>
  );
}
